import { useEffect, useMemo, useState } from "react";
import type { TranscriptMessage } from "@/pages/agent-interfaces/live-agent/components/TranscriptPanel";
import type { RailItem } from "@/types/voice/resources-rail";

const MAX_SUGGESTED = 3;

const toMessage = (item: RailItem, timestamp: Date): TranscriptMessage => ({
  message: "",
  isUser: false,
  timestamp,
  attachment: {
    kind: "link",
    url: item.url,
    title: item.label,
    description: item.meta ?? null,
    source: "resource",
  },
});

/**
 * The chat's message list with the suggested resources placed in it as cards,
 * at the point of the conversation where they became available.
 *
 * The cards are display-only: the conversation's own `messages` state is left
 * alone, because it also drives the wrap-up, the capture moment, the shared
 * media card and the resource filter. They are taken once per conversation,
 * after the visitor's first message, so a later filter pass never reshuffles
 * what is already on screen, and they clear when the message list does.
 */
export const useSuggestedResourceMessages = (
  messages: TranscriptMessage[],
  items: RailItem[],
  conversationActive: boolean,
): TranscriptMessage[] => {
  const [suggested, setSuggested] = useState<TranscriptMessage[]>([]);

  const hasMessages = messages.length > 0;
  const visitorSpoke = useMemo(() => messages.some((message) => message.isUser), [messages]);

  useEffect(() => {
    if (!hasMessages) setSuggested([]);
  }, [hasMessages]);

  useEffect(() => {
    if (!conversationActive || !visitorSpoke || items.length === 0) return;
    setSuggested((current) => {
      if (current.length > 0) return current;
      const shownAt = new Date();
      return items.slice(0, MAX_SUGGESTED).map((item) => toMessage(item, shownAt));
    });
  }, [conversationActive, visitorSpoke, items]);

  return useMemo(() => {
    if (!hasMessages || suggested.length === 0) return messages;
    const shownAt = suggested[0].timestamp.getTime();
    const insertAt = messages.findIndex((message) => message.timestamp.getTime() > shownAt);
    if (insertAt === -1) return [...messages, ...suggested];
    return [...messages.slice(0, insertAt), ...suggested, ...messages.slice(insertAt)];
  }, [messages, suggested, hasMessages]);
};

export default useSuggestedResourceMessages;
